"use strict";
import { clamp } from "lodash";
function getOverlayPosition({
  position = "bottom",
  targetRect,
  overlayRect,
  offset = 0,
  caretSize = 0,
  outerMargin = 0,
  sticky,
  alignment = "center"
}) {
  const { innerWidth, innerHeight } = window;
  if (!targetRect || !overlayRect) {
    return {
      overlay: { top: 0, left: 0 },
      caret: { top: 0, left: 0 },
      modifier: position
    };
  }
  const overlayWidth = overlayRect.width;
  const overlayHeight = overlayRect.height;
  const offsetWithCaret = offset + caretSize;
  let modifier = position;
  const overlays = {
    top: {
      top: targetRect.top - overlayHeight - offsetWithCaret,
      left: alignment === "start" ? targetRect.left : targetRect.left + targetRect.width / 2 - overlayWidth / 2
    },
    right: {
      top: alignment === "start" ? targetRect.top : targetRect.top + targetRect.height / 2 - overlayHeight / 2,
      left: targetRect.right + offsetWithCaret
    },
    bottom: {
      top: targetRect.bottom + offsetWithCaret,
      left: alignment === "start" ? targetRect.left : targetRect.left + targetRect.width / 2 - overlayWidth / 2
    },
    left: {
      top: alignment === "start" ? targetRect.top : targetRect.top + targetRect.height / 2 - overlayHeight / 2,
      left: targetRect.left - overlayWidth - offsetWithCaret
    }
  };
  const caretVerticalAxisPosition = alignment === "start" ? targetRect.height / 2 - caretSize : overlayHeight / 2 - caretSize;
  const caretHorizontalAxisPosition = alignment === "start" ? targetRect.width / 2 - caretSize : overlayWidth / 2 - caretSize;
  const carets = {
    top: {
      top: overlayHeight,
      left: caretHorizontalAxisPosition
    },
    right: {
      top: caretVerticalAxisPosition,
      left: -caretSize
    },
    bottom: {
      top: -caretSize,
      left: caretHorizontalAxisPosition
    },
    left: {
      top: caretVerticalAxisPosition,
      left: overlayWidth
    }
  };
  const preferredOverlay = overlays[position];
  const caret = carets[position];
  const isTopOrBottom = position === "top" || position === "bottom";
  const isLeftOrRight = position === "left" || position === "right";
  function spaceAboveOverlay(o) {
    return o.top - outerMargin;
  }
  function spaceBelowOverlay(o) {
    return innerHeight - outerMargin - (o.top + overlayHeight);
  }
  if (spaceAboveOverlay(preferredOverlay) < 0) {
    if (isLeftOrRight) {
      const caretMin = sticky ? 0 : targetRect.height / 2 - caretSize;
      caret.top = clamp(preferredOverlay.top + overlayHeight / 2 - caretSize - outerMargin, caretMin, overlayHeight);
      preferredOverlay.top = sticky ? outerMargin : Math.min(outerMargin, targetRect.top);
    } else {
      if (sticky) {
        preferredOverlay.top = Math.max(
          outerMargin,
          Math.min(innerHeight - overlayHeight - offsetWithCaret, overlays.bottom.top)
        );
        caret.top = carets.bottom.top;
        modifier = "bottom";
      } else {
        if (spaceAboveOverlay(preferredOverlay) > spaceBelowOverlay(overlays.bottom)) {
          modifier = "top";
        } else {
          modifier = "bottom";
        }
        preferredOverlay.top = overlays[modifier].top;
        caret.top = carets[modifier].top;
      }
    }
  } else if (spaceBelowOverlay(preferredOverlay) < 0) {
    if (isLeftOrRight) {
      const overlap = preferredOverlay.top + overlayHeight - innerHeight;
      const caretMax = sticky ? overlayHeight - caretSize * 2 : Math.min(overlayHeight - caretSize * 2, overlayHeight - targetRect.height / 2 - caretSize);
      caret.top = clamp(overlayHeight / 2 + overlap, 0, caretMax);
      preferredOverlay.top = sticky ? innerHeight - overlayHeight - outerMargin : Math.max(innerHeight - overlayHeight - outerMargin, targetRect.bottom - overlayHeight);
    } else {
      if (sticky) {
        preferredOverlay.top = Math.max(
          Math.min(innerHeight - overlayHeight - offsetWithCaret, overlays.top.top),
          outerMargin
        );
        caret.top = carets.top.top;
        modifier = "top";
      } else {
        if (spaceAboveOverlay(overlays.top) > spaceBelowOverlay(preferredOverlay)) {
          modifier = "top";
        } else {
          modifier = "bottom";
        }
        preferredOverlay.top = overlays[modifier].top;
        caret.top = carets[modifier].top;
      }
    }
  }
  if (preferredOverlay.left + overlayWidth > innerWidth - outerMargin) {
    if (isTopOrBottom) {
      const overlap = preferredOverlay.left + overlayWidth - innerWidth;
      caret.left = clamp(overlayWidth / 2 + overlap - caretSize + outerMargin, 0, overlayWidth);
      preferredOverlay.left = innerWidth - outerMargin - overlayWidth;
    } else {
      preferredOverlay.left = Math.max(outerMargin, overlays.left.left);
      caret.left = carets.left.left;
      modifier = "left";
    }
  } else if (preferredOverlay.left < outerMargin) {
    if (isTopOrBottom) {
      caret.left = clamp(preferredOverlay.left + overlayWidth / 2 - caretSize - outerMargin, 0, overlayWidth);
      preferredOverlay.left = outerMargin;
    } else {
      preferredOverlay.left = Math.min(innerWidth - overlayWidth - offsetWithCaret, overlays.right.left);
      caret.left = carets.right.left;
      modifier = "right";
    }
  }
  return { modifier, overlay: preferredOverlay, caret };
}
export default getOverlayPosition;
